import { Formik } from 'formik';
import classnames from 'classnames';
import { useDispatch } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { setModals } from '@/store/actions/commonActions';
import { MODAL_TYPES } from '@/components/ModalProvider';
import Checkbox from '@/components/UI/Checkbox';
import { MyLink } from '@/components/MyLink';
import { APP_LINKS } from '@/helpers/links';
import { ROLES } from '@/helpers/constants';
import { useGetFundraisingStatusById } from '@/query/queries/fundraising/useGetFundraisingStatusById';
import { useResetFundraising } from '@/query/queries/fundraising/useResetFundraising';
import { useConfirmFundraising } from '@/query/queries/fundraising/useConfirmFundraising';
import { BackButton } from '@/components/BackButton';
import { FR_STEPS } from '@/helpers/fundraising';
import { useGetMe } from '@/hooks/shared/useGetMe';
import { FundraisingStatus } from '../FundraisingStatus';
import { ApplicationMessages } from '../ApplicationMessages';
import { FoundersConfirmation } from '../FoundersConfirmation';
import { LoanTermStatus } from '../LoanTerms';
import { CO_FOUNDER_VALIDATION_SCHEMA } from '../../schema';
import IconEdit from '../../assets/icon_edit.svg';
import r from '@/kit/RoundButton/button.module.scss';
import s from './index.module.scss';

export const CoFounderView = ({ disabled, fundraisingId, onBack }) => {
  const { isCoFounder } = useGetMe();
  const { data: status } = useGetFundraisingStatusById(fundraisingId);
  const { mutateAsync: confirmFundraising } = useConfirmFundraising();
  const dispatch = useDispatch();
  const { mutateAsync: resetFundraising } = useResetFundraising();
  const { t } = useTranslation('fundraising');

  const onSubmit = async (values, formik) => {
    const payload = { status: values.has_reviewed_settings && values.has_accepted_terms };
    await confirmFundraising(payload);
    formik.setSubmitting(false);
  };

  const onReset = async () => {
    dispatch(
      setModals({
        type: MODAL_TYPES.confirm_modal,
        onConfirm: async () => {
          await resetFundraising();
          dispatch(setModals([]));
        },
        acceptLabel: t('fundraising:shared.reset'),
        cancelLabel: t('fundraising:shared.cancel'),
        title: t('fundraising:shared.changed_your_mind'),
        desc: t('fundraising:co_founder_view.opt_out_before_start'),
      }),
    );
  };

  const canReset = Boolean(status?.step === FR_STEPS.fundraising_summary && !status?.is_active && status?.is_confirmed);

  return (
    <Formik
      enableReinitialize
      onSubmit={onSubmit}
      validationSchema={CO_FOUNDER_VALIDATION_SCHEMA(t)}
      initialValues={{
        new_funds: status?.new_funds || 0,
        tariff_plan: status?.tariff_plan || null,
        fpg: status?.fpg_option || null,
        has_accepted_terms: false,
        has_reviewed_settings: false,
      }}
    >
      {({ values, errors, touched, isSubmitting, setFieldValue, handleSubmit }) => (
        <form onSubmit={handleSubmit}>
          <div className="container-inner" style={{ margin: '50px 0' }}>
            <div className={s.wrapper}>
              <BackButton onClick={onBack} />
              <h1 className="f-hero-h2">{t('page.title')}</h1>
              <ApplicationMessages
                fundraisingId={fundraisingId}
                role={ROLES.co_founder}
                className={s.application}
              />
              {status?.step === FR_STEPS.set_goal && (
                <h1 className="f-h2 f-500">{t('co_founder_view.wait_for_admin_setup')}</h1>
              )}
              {status?.step === FR_STEPS.fundraising_summary && !status?.is_confirmed && (
                <h1 className="f-h2 f-500">{t('co_founder_view.confirm_participation')}</h1>
              )}
              {isCoFounder && canReset && (
                <button
                  type="button"
                  onClick={onReset}
                  className={classnames(r.black_small, s.btn_reset)}
                >
                  {t('fundraising:shared.reset')}{' '}
                  <img src={IconEdit} alt="reset" width={24} height={24} />
                </button>
              )}
              <FundraisingStatus
                fundraisingId={fundraisingId}
                disabled={disabled}
                className={s.stats}
                newFunds={values.new_funds}
              />
              {isCoFounder &&
                status &&
                status.step === FR_STEPS.fundraising_summary &&
                !status?.is_confirmed && (
                  <div className={s.confirmation}>
                    <Checkbox
                      name="has_reviewed_settings"
                      isChecked={values.has_reviewed_settings}
                      onChange={() =>
                        setFieldValue('has_reviewed_settings', !values.has_reviewed_settings)
                      }
                      error={errors.has_reviewed_settings}
                      render={() => (
                        <span
                          className={classnames(
                            'f-12',
                            touched.has_reviewed_settings &&
                              errors.has_reviewed_settings &&
                              s.error,
                          )}
                        >
                          {t('co_founder_view.review_and_agree')}
                        </span>
                      )}
                    />
                    <Checkbox
                      name="has_accepted_terms"
                      isChecked={values.has_accepted_terms}
                      onChange={() =>
                        setFieldValue('has_accepted_terms', !values.has_accepted_terms)
                      }
                      error={errors.has_accepted_terms}
                      render={() => (
                        <span
                          className={classnames(
                            'f-12',
                            touched.has_accepted_terms && errors.has_accepted_terms && s.error,
                          )}
                        >
                          <Trans
                            ns="fundraising"
                            i18nKey="co_founder_view.agree_terms"
                            components={{
                              LinkTerms: (
                                <MyLink href={APP_LINKS.docTermsOfUse} underline isExternal />
                              ),
                              LinkAssigmentAgreement: (
                                <MyLink
                                  href={APP_LINKS.docAssignmentAgreement}
                                  underline
                                  isExternal
                                />
                              ),
                              LinkSuretyAgreement: (
                                <MyLink href={APP_LINKS.suretyAgreement} underline isExternal />
                              ),
                            }}
                          />
                        </span>
                      )}
                    />
                    <button
                      type="submit"
                      disabled={disabled || isSubmitting}
                      className={classnames(r.yellow_large, s.btn_submit)}
                      onSubmit={handleSubmit}
                    >
                      {t('co_founder_view.confirm_participation')}
                    </button>
                  </div>
                )}

              {status?.step === FR_STEPS.fundraising_summary && (
                <>
                  <LoanTermStatus status={status} />
                  <FoundersConfirmation />
                </>
              )}
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
};
